import React, { useState } from 'react';
import { Book, PenTool, Building2, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

/**
 * Role Selection Modal for multi-role users
 * Displays after login when user has 2+ identity roles
 * 
 * Identity roles: reader, creator (author), publisher, admin
 * Priority order: admin > publisher > creator > reader
 * Note: Backend sends "author", frontend normalizes to "creator"
 */

// Role display configuration
// Note: "author" maps to same config as "creator" - they are equivalent
const ROLE_CONFIG = {
  reader: {
    label: 'Reader',
    description: 'Browse and listen to interactive books',
    icon: Book,
    buttonText: 'Continue as Reader',
    gradient: 'from-blue-600 to-cyan-600',
    hoverGradient: 'hover:from-blue-500 hover:to-cyan-500'
  },
  creator: {
    label: 'Author',  // Display "Author", not "creator"
    description: 'Manage your manuscripts and earnings',
    icon: PenTool,
    buttonText: 'Continue as Author',
    gradient: 'from-emerald-600 to-teal-600',
    hoverGradient: 'hover:from-emerald-500 hover:to-teal-500'
  },
  author: {
    label: 'Author',  // Same as creator - backend sends "author"
    description: 'Manage your manuscripts and earnings',
    icon: PenTool,
    buttonText: 'Continue as Author',
    gradient: 'from-emerald-600 to-teal-600',
    hoverGradient: 'hover:from-emerald-500 hover:to-teal-500'
  },
  publisher: {
    label: 'Publisher',
    description: 'Manage authors and catalog',
    icon: Building2,
    buttonText: 'Continue as Publisher',
    gradient: 'from-purple-600 to-indigo-600',
    hoverGradient: 'hover:from-purple-500 hover:to-indigo-500'
  },
  admin: {
    label: 'Admin',
    description: 'Platform administration',
    icon: Settings,
    buttonText: 'Continue as Admin',
    gradient: 'from-yellow-600 to-orange-600',
    hoverGradient: 'hover:from-yellow-500 hover:to-orange-500'
  }
};

// Role priority for determining primary/secondary button styling
// Both "author" and "creator" are equivalent
const ROLE_PRIORITY = ['admin', 'publisher', 'author', 'creator', 'reader'];

// Normalize role - treat "author" as "creator" for frontend consistency
const normalizeRole = (role) => {
  if (role === 'author') return 'creator';
  return role;
};

const RoleSelectionModal = ({ 
  isOpen, 
  userDisplayName, 
  identityRoles, 
  onSelectRole 
}) => {
  const [rememberChoice, setRememberChoice] = useState(false);

  if (!isOpen || !identityRoles || identityRoles.length < 2) {
    return null;
  }

  // Normalize roles: "author" → "creator" for frontend consistency
  // Also dedupe in case both author and creator are present
  const normalizedRoles = [...new Set(identityRoles.map(normalizeRole))];
  
  // If after normalization we have less than 2 roles, don't show modal
  if (normalizedRoles.length < 2) {
    return null;
  }

  // Sort roles by priority (highest first)
  const sortedRoles = [...normalizedRoles].sort((a, b) => {
    const aIndex = ROLE_PRIORITY.indexOf(a);
    const bIndex = ROLE_PRIORITY.indexOf(b);
    // Handle roles not in priority list
    const aVal = aIndex === -1 ? 999 : aIndex;
    const bVal = bIndex === -1 ? 999 : bIndex;
    return aVal - bVal;
  });

  const primaryRole = sortedRoles[0];
  const secondaryRoles = sortedRoles.slice(1);

  // Handle role selection with optional "remember" persistence
  const handleRoleSelect = (role) => {
    if (rememberChoice) {
      localStorage.setItem('preferredRole', role);
    }
    onSelectRole(role);
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {/* Prevent closing */}}>
      <DialogContent 
        className="sm:max-w-md bg-slate-900 border-slate-700 text-white"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton={true}
      >
        <DialogHeader className="text-center pb-2">
          <DialogTitle className="text-2xl font-bold text-white">
            Welcome back{userDisplayName ? `, ${userDisplayName}` : ''}!
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-base">
            How would you like to proceed?
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-4">
          {/* Primary role button (highest priority) */}
          {ROLE_CONFIG[primaryRole] && (
            <Button
              onClick={() => handleRoleSelect(primaryRole)}
              className={`w-full py-6 text-lg font-semibold rounded-xl bg-gradient-to-r ${ROLE_CONFIG[primaryRole].gradient} ${ROLE_CONFIG[primaryRole].hoverGradient} text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3`}
              data-testid={`role-select-${primaryRole}`}
            >
              {React.createElement(ROLE_CONFIG[primaryRole].icon, { className: 'w-6 h-6' })}
              {ROLE_CONFIG[primaryRole].buttonText}
            </Button>
          )}

          {/* Secondary role buttons */}
          {secondaryRoles.map(role => (
            ROLE_CONFIG[role] && (
              <Button
                key={role}
                onClick={() => handleRoleSelect(role)}
                variant="outline"
                className="w-full py-5 text-base font-medium rounded-xl border-slate-600 text-slate-200 hover:bg-slate-800 hover:border-slate-500 transition-all flex items-center justify-center gap-3"
                data-testid={`role-select-${role}`}
              >
                {React.createElement(ROLE_CONFIG[role].icon, { className: 'w-5 h-5 text-slate-400' })}
                {ROLE_CONFIG[role].buttonText}
              </Button>
            )
          ))}
        </div>

        {/* Remember my choice checkbox */}
        <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-slate-700">
          <input
            type="checkbox"
            id="remember-role"
            checked={rememberChoice}
            onChange={(e) => setRememberChoice(e.target.checked)}
            className="w-4 h-4 rounded border-slate-500 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-slate-900 cursor-pointer"
            data-testid="remember-role-checkbox"
          />
          <label 
            htmlFor="remember-role" 
            className="text-sm text-slate-400 cursor-pointer select-none"
          >
            Remember my choice
          </label>
        </div>

        <p className="text-xs text-slate-500 text-center mt-2">
          You can switch roles anytime from the navigation menu
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default RoleSelectionModal;
